import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import './Search.css';

const Search = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('users'); // Changed from 'people' to 'users'
    const [users, setUsers] = useState([]);
    const [songs, setSongs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const navigate = useNavigate();
    const debounceTimerRef = useRef(null);
    const usersUnsubscribeRef = useRef(null);
    const songsUnsubscribeRef = useRef(null);

    // Search users by displayName or name (case-insensitive using range query)
    const searchUsers = useCallback((termLower) => {
        setLoading(true);
        try {
            const usersCol = collection(db, 'users');
            
            // Query 1: Search by nameLower field (if it exists in Firestore)
            // This uses range query: >= termLower AND <= termLower + "\uf8ff"
            // This effectively does a "starts with" case-insensitive search
            const q1 = query(
                usersCol,
                where('nameLower', '>=', termLower),
                where('nameLower', '<=', termLower + '\uf8ff'),
                limit(50)
            );

            // Query 2: Search by displayName (fallback if nameLower doesn't exist)
            // Note: Firestore range queries work on the field as stored, so we need lowercase version
            // If displayNameLower exists, use it; otherwise we'll filter client-side
            let q2 = null;
            try {
                q2 = query(
                    usersCol,
                    where('displayNameLower', '>=', termLower),
                    where('displayNameLower', '<=', termLower + '\uf8ff'),
                    limit(50)
                );
            } catch (err) {
                // If displayNameLower field doesn't exist, we'll handle it differently
                console.log('displayNameLower field not available, using nameLower only');
            }

            // Use onSnapshot for real-time updates
            const unsub1 = onSnapshot(
                q1,
                (snapshot) => {
                    const results = [];
                    snapshot.forEach((doc) => {
                        const userData = { id: doc.id, ...doc.data() };
                        // Additional client-side filtering to ensure match
                        const name = (userData.name || userData.displayName || userData.fullName || '').toLowerCase();
                        if (name.startsWith(termLower)) {
                            results.push(userData);
                        }
                    });

                    // If we have a second query, merge results using getDocs (one-time, no duplicate listener)
                    if (q2) {
                        getDocs(q2)
                            .then((snapshot2) => {
                                const results2 = [];
                                snapshot2.forEach((doc) => {
                                    const userData = { id: doc.id, ...doc.data() };
                                    const name = (userData.name || userData.displayName || userData.fullName || '').toLowerCase();
                                    if (name.startsWith(termLower)) {
                                        results2.push(userData);
                                    }
                                });

                                // Merge and deduplicate results
                                const merged = [...results, ...results2];
                                const unique = merged.filter((user, index, self) =>
                                    index === self.findIndex((u) => u.id === user.id)
                                );
                                setUsers(unique);
                                setLoading(false);
                                setSearching(false);
                            })
                            .catch((err) => {
                                console.error('Error in displayName query:', err);
                                setUsers(results);
                                setLoading(false);
                                setSearching(false);
                            });
                    } else {
                        setUsers(results);
                        setLoading(false);
                        setSearching(false);
                    }
                },
                (err) => {
                    console.error('Error in nameLower query:', err);
                    // Fallback: fetch all users and filter client-side
                    getDocs(usersCol).then((snapshot) => {
                        const allUsers = [];
                        snapshot.forEach((doc) => {
                            const userData = { id: doc.id, ...doc.data() };
                            const name = (userData.name || userData.displayName || userData.fullName || '').toLowerCase();
                            if (name.startsWith(termLower)) {
                                allUsers.push(userData);
                            }
                        });
                        setUsers(allUsers);
                        setLoading(false);
                        setSearching(false);
                    });
                }
            );
            usersUnsubscribeRef.current = unsub1;
        } catch (err) {
            console.error('Error searching users:', err);
            setUsers([]);
            setLoading(false);
            setSearching(false);
        }
    }, []);

    // Search songs by title or artistName (case-insensitive)
    const searchSongs = useCallback((termLower) => {
        setLoading(true);
        try {
            const songsCol = collection(db, 'songs');

            // Query 1: Search by title (using titleLower if it exists, otherwise client-side filter)
            // For now, we'll fetch recent songs and filter client-side since titleLower might not exist
            const q1 = query(songsCol, orderBy('createdAt', 'desc'), limit(200));

            // Use onSnapshot for real-time updates
            const unsub = onSnapshot(q1, (snapshot) => {
                const results = [];
                snapshot.forEach((doc) => {
                    const songData = { id: doc.id, ...doc.data() };
                    const title = (songData.title || songData.name || '').toLowerCase();
                    const artistName = (songData.artistName || songData.artist || songData.userName || '').toLowerCase();
                    
                    // Match if title or artistName starts with search term
                    if (title.startsWith(termLower) || artistName.startsWith(termLower)) {
                        results.push(songData);
                    }
                });

                // Sort by relevance (exact title matches first)
                results.sort((a, b) => {
                    const aTitle = (a.title || '').toLowerCase();
                    const bTitle = (b.title || '').toLowerCase();
                    const aExact = aTitle === termLower;
                    const bExact = bTitle === termLower;
                    if (aExact && !bExact) return -1;
                    if (!aExact && bExact) return 1;
                    return aTitle.localeCompare(bTitle);
                });

                setSongs(results.slice(0, 50)); // Limit to 50 results
                setLoading(false);
                setSearching(false);
            }, (err) => {
                console.error('Error searching songs:', err);
                // Fallback: try without orderBy
                getDocs(songsCol).then((snapshot) => {
                    const allSongs = [];
                    snapshot.forEach((doc) => {
                        const songData = { id: doc.id, ...doc.data() };
                        const title = (songData.title || songData.name || '').toLowerCase();
                        const artistName = (songData.artistName || songData.artist || songData.userName || '').toLowerCase();
                        if (title.startsWith(termLower) || artistName.startsWith(termLower)) {
                            allSongs.push(songData);
                        }
                    });
                    setSongs(allSongs.slice(0, 50));
                    setLoading(false);
                    setSearching(false);
                });
            });

            songsUnsubscribeRef.current = unsub;
        } catch (err) {
            console.error('Error searching songs:', err);
            setSongs([]);
            setLoading(false);
            setSearching(false);
        }
    }, []);

    // Perform Firestore search based on active tab
    const performSearch = useCallback((term) => {
        if (!term) {
            setUsers([]);
            setSongs([]);
            setSearching(false);
            return;
        }

        const termLower = term.toLowerCase();

        if (activeTab === 'users') {
            searchUsers(termLower);
        } else {
            searchSongs(termLower);
        }
    }, [activeTab, searchUsers, searchSongs]);

    // Debounced search function - waits 300ms before querying Firestore
    useEffect(() => {
        // Clear previous timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Clear previous subscriptions
        if (usersUnsubscribeRef.current) {
            usersUnsubscribeRef.current();
            usersUnsubscribeRef.current = null;
        }
        if (songsUnsubscribeRef.current) {
            songsUnsubscribeRef.current();
            songsUnsubscribeRef.current = null;
        }

        // Reset results if search term is empty
        if (!searchTerm.trim()) {
            setUsers([]);
            setSongs([]);
            setSearching(false);
            return;
        }

        // Set searching state
        setSearching(true);

        // Debounce: wait 300ms before executing search
        debounceTimerRef.current = setTimeout(() => {
            performSearch(searchTerm.trim());
        }, 300);

        // Cleanup function
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            if (usersUnsubscribeRef.current) {
                usersUnsubscribeRef.current();
                usersUnsubscribeRef.current = null;
            }
            if (songsUnsubscribeRef.current) {
                songsUnsubscribeRef.current();
                songsUnsubscribeRef.current = null;
            }
        };
    }, [searchTerm, activeTab, performSearch]);

    // Handle navigation to user profile
    const handleViewProfile = (userId) => {
        navigate(`/profile/${userId}`);
    };

    // Handle navigation to artist profile
    const handleViewArtist = (artistId) => {
        if (artistId) {
            navigate(`/profile/${artistId}`);
        }
    };

    return (
        <div className="min-h-screen w-full bg-[#0e0e0e] text-white pb-8">
            <div className="mx-auto max-w-2xl px-4 py-6">
                {/* Header with Search Bar */}
                <header className="mb-6">
                    <h1 className="text-3xl font-extrabold mb-4 text-[#ff2a2a]" style={{ textShadow: '0 0 10px rgba(255, 42, 42, 0.5)' }}>
                        Search
                    </h1>
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Search by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-3 pr-12 rounded-full bg-[#1a1a1a] border border-[#333] text-white placeholder-[#888] focus:outline-none focus:border-[#ff2a2a] focus:ring-2 focus:ring-[#ff2a2a]/30 transition-all"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#888] text-xl">
                            🔍
                        </span>
                    </div>
                </header>

                {/* Tabs */}
                <div className="flex border-b border-[#333] mb-6">
                    <button
                        className={`flex-1 py-3 px-4 text-center font-semibold transition-all ${
                            activeTab === 'users'
                                ? 'text-[#ff2a2a] border-b-2 border-[#ff2a2a]'
                                : 'text-[#888] hover:text-white'
                        }`}
                        onClick={() => setActiveTab('users')}
                    >
                        Users
                    </button>
                    <button
                        className={`flex-1 py-3 px-4 text-center font-semibold transition-all ${
                            activeTab === 'songs'
                                ? 'text-[#ff2a2a] border-b-2 border-[#ff2a2a]'
                                : 'text-[#888] hover:text-white'
                        }`}
                        onClick={() => setActiveTab('songs')}
                    >
                        Songs
                    </button>
                </div>

                {/* Loading/Searching Indicator */}
                {(loading || searching) && (
                    <div className="text-center py-8 text-[#888]">
                        Searching...
                    </div>
                )}

                {/* Results */}
                <div className="space-y-4">
                    {activeTab === 'users' ? (
                        // Users Tab
                        <div>
                            {!loading && !searching && users.length === 0 && searchTerm.trim() && (
                                <div className="text-center py-12 text-[#888]">
                                    No users found
                                </div>
                            )}
                            {users.map((user) => (
                                <div
                                    key={user.id}
                                    className="rounded-xl bg-[#1a1a1a] p-4 shadow-lg hover:bg-[#222] transition-all cursor-pointer border border-[#333] hover:border-[#ff2a2a]/50"
                                    onClick={() => handleViewProfile(user.id)}
                                >
                                    <div className="flex items-center gap-4">
                                        {/* Profile Photo */}
                                        <div className="w-16 h-16 rounded-full overflow-hidden bg-[#2a2a2a] flex items-center justify-center flex-shrink-0">
                                            {user.photoURL ? (
                                                <img
                                                    src={user.photoURL}
                                                    alt={user.name || user.displayName}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="text-2xl font-bold text-white">
                                                    {(user.name || user.displayName || 'U').charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                        </div>

                                        {/* User Info */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="text-lg font-semibold text-white truncate">
                                                    {user.name || user.displayName || user.fullName || 'Unknown User'}
                                                </h3>
                                                {user.role && (
                                                    <span className="px-2 py-1 rounded-full bg-[#ff2a2a] text-white text-xs font-semibold">
                                                        {user.role === 'artist' ? '🎤 Artist' : '🎧 Listener'}
                                                    </span>
                                                )}
                                            </div>
                                            {user.email && (
                                                <p className="text-sm text-[#888] truncate">{user.email}</p>
                                            )}
                                        </div>

                                        {/* View Profile Button */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewProfile(user.id);
                                            }}
                                            className="px-4 py-2 rounded-full bg-[#ff2a2a] text-white font-semibold hover:bg-[#ff1111] transition-all flex-shrink-0"
                                        >
                                            View Profile
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        // Songs Tab
                        <div>
                            {!loading && !searching && songs.length === 0 && searchTerm.trim() && (
                                <div className="text-center py-12 text-[#888]">
                                    No songs found
                                </div>
                            )}
                            {songs.map((song) => (
                                <div
                                    key={song.id}
                                    className="rounded-xl bg-[#1a1a1a] p-4 shadow-lg hover:bg-[#222] transition-all border border-[#333] hover:border-[#ff2a2a]/50"
                                >
                                    <div className="flex items-start gap-4">
                                        {/* Song Image */}
                                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-[#2a2a2a] flex items-center justify-center flex-shrink-0">
                                            {song.coverImage || song.thumbnail ? (
                                                <img
                                                    src={song.coverImage || song.thumbnail}
                                                    alt={song.title || song.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <span className="text-3xl">🎵</span>
                                            )}
                                        </div>

                                        {/* Song Info */}
                                        <div className="flex-1 min-w-0">
                                            <h3
                                                className="text-lg font-semibold text-white mb-1 cursor-pointer hover:text-[#ff2a2a] transition-colors"
                                                onClick={() => handleViewArtist(song.artistId || song.userId || song.ownerId)}
                                            >
                                                {song.title || song.name || 'Untitled Song'}
                                            </h3>
                                            <p
                                                className="text-sm text-[#888] mb-2 cursor-pointer hover:text-[#ff2a2a] transition-colors"
                                                onClick={() => handleViewArtist(song.artistId || song.userId || song.ownerId)}
                                            >
                                                By {song.artistName || song.artist || song.userName || 'Unknown Artist'}
                                            </p>

                                            {/* Audio Player */}
                                            {song.songUrl && (
                                                <audio
                                                    controls
                                                    className="w-full mt-2"
                                                    style={{ maxWidth: '100%' }}
                                                >
                                                    <source src={song.songUrl} type="audio/mpeg" />
                                                    Your browser does not support the audio element.
                                                </audio>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Search;
